import React, { useEffect, useState } from 'react';
import { CheckCircle, Shield, Lock, FileText, UserCheck, Building } from 'lucide-react';

const VerificationScreen = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    { icon: <FileText className="w-5 h-5" />, text: "Reviewing application details", delay: 0 },
    { icon: <UserCheck className="w-5 h-5" />, text: "Verifying identity and income", delay: 700 },
    { icon: <Shield className="w-5 h-5" />, text: "Checking eligibility criteria", delay: 1400 },
    { icon: <Building className="w-5 h-5" />, text: "Matching with approved lenders", delay: 2100 }
  ];

  useEffect(() => {
    steps.forEach((step, index) => {
      setTimeout(() => {
        setCurrentStep(index + 1);
      }, step.delay);
    });

    const timer = setTimeout(() => {
      onComplete();
    }, 2800);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onComplete]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
          
          {/* Header */}
          <div className="bg-slate-50 border-b border-slate-200 px-8 py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 border border-emerald-200 rounded-2xl mb-4">
              <Shield className="w-8 h-8 text-emerald-600 animate-pulse" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              Verifying Your Application
            </h2>
            <p className="text-slate-600">
              Please wait while we securely process your information
            </p>
          </div>

          {/* Progress */}
          <div className="p-8">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-700">Processing</span>
                <span className="text-sm font-semibold text-emerald-600">{Math.round((currentStep / steps.length) * 100)}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(currentStep / steps.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Steps */}
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-4 transition-all duration-300 ${
                    currentStep > index ? 'opacity-100' : 'opacity-40'
                  }`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    currentStep > index 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-slate-200 text-slate-400'
                  }`}>
                    {currentStep > index ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : currentStep === index ? (
                      <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      step.icon
                    )}
                  </div>
                  <span className={`text-sm font-medium transition-colors ${
                    currentStep > index ? 'text-slate-900' : 'text-slate-500'
                  }`}>
                    {step.text}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                <Lock className="w-4 h-4 text-emerald-600" />
                <span>256-bit SSL Encryption Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationScreen;
